import React, { useEffect, useMemo, useState } from 'react';
import { IonAvatar, IonLabel, IonModal } from '@ionic/react';
import { personAddOutline } from 'ionicons/icons';
import { MemoryRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ContactListItem } from '@rsmaids/shared/types/contacts';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import SheetModal from './SheetModal';
import NewContact from '../pages/bookingSystem/NewContact';

const CONTACTS_STALE_MS = 10 * 60 * 1000;

const sortName = (c: ContactListItem) => `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.primaryEmail || '';
const sortContacts = (list: ContactListItem[]) => [...list].sort((a, b) => sortName(a).localeCompare(sortName(b)));

// The contacts list the contacts page keeps on this device
function readCachedContacts(): ContactListItem[] {
  try { return sortContacts(JSON.parse(localStorage.getItem('cachedContacts') || '[]') as ContactListItem[]); } catch { return []; }
}

// The last full fetch made by a picker, so reopening one soon after doesn't fetch again
let fetched: { at: number; contacts: ContactListItem[] } | undefined;
const hasFreshFetch = () => !!fetched && Date.now() - fetched.at < CONTACTS_STALE_MS;

interface ContactPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectContact: (contact: ContactListItem) => void;
  title?: string;
  searchPlaceholder?: string;
  /** Set when the host renders outside the app's router (the softphone): the add-contact form then gets its own. */
  outsideRouter?: boolean;
}

/**
 * A bottom sheet to pick a contact: avatar + name rows, a search box (matches name, phone and email) and an
 * add-contact button in the header. Always rendered by the host and toggled by `isOpen`, so Ionic runs its
 * present / dismiss transitions.
 */
const ContactPickerSheet: React.FC<ContactPickerSheetProps> = ({ isOpen, onClose, onSelectContact, title, searchPlaceholder, outsideRouter = false }) => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<ContactListItem[]>([]);
  const [showNewContact, setShowNewContact] = useState(false);

  // Each time the sheet opens, start from the contacts already on this device so it fills at once
  const [wasOpen, setWasOpen] = useState(false);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setContacts(hasFreshFetch() ? fetched!.contacts : readCachedContacts());
  }

  // ...and fetch them when there are none on the device (or they are over 10 minutes old), so a new contact shows up
  useEffect(() => {
    if (!isOpen || hasFreshFetch()) return;
    const lastLoad = Number(localStorage.getItem('contactsLastLoadTime') || 0);
    if (readCachedContacts().length > 0 && Date.now() - lastLoad <= CONTACTS_STALE_MS) return;
    let cancelled = false;
    DataServiceFactory.getService().contacts.getAllContacts()
      .then((all) => {
        fetched = { at: Date.now(), contacts: sortContacts(all) };
        if (!cancelled) setContacts(fetched.contacts);
      })
      .catch((err) => console.error('[ContactPickerSheet] could not load contacts:', err));
    return () => { cancelled = true; };
  }, [isOpen]);

  // One searchable string per contact, plus the way back from that string to the contact
  const { items, contactByItem } = useMemo(() => {
    const byItem = new Map<string, ContactListItem>();
    for (const c of contacts) {
      const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.displayName || c.primaryEmail || '';
      byItem.set([name, c.primaryPhone || '', c.primaryEmail || '', c.id].join(' | '), c);
    }
    return { items: [...byItem.keys()], contactByItem: byItem };
  }, [contacts]);

  // Closing the add-contact form: forget the load times so the next open refetches (the new contact shows up)
  const closeNewContact = () => {
    fetched = undefined;
    localStorage.removeItem('contactsLastLoadTime');
    setShowNewContact(false);
  };

  return (
    <>
      <SheetModal
        isOpen={isOpen}
        onClose={onClose}
        title={title ?? t('components.ContactsListModal.selectContact')}
        menuOptions={[]}
        headerAction={{
          icon: personAddOutline,
          label: t('components.ContactsListModal.addNewContact'),
          onClick: () => { onClose(); setShowNewContact(true); },
        }}
        isSelector
        selectorItems={items}
        searchPlaceholder={searchPlaceholder ?? t('components.ContactsListModal.searchContacts', { length: contacts.length })}
        selectorEmptyText={t('components.ContactsListModal.noContactsFound')}
        onSelectorItemClick={(item) => {
          const contact = contactByItem.get(item);
          if (contact) onSelectContact(contact);
        }}
        renderSelectorItem={(item) => {
          // item = "name | phone | email | id" (the whole string is what the search matches); the row shows avatar + name
          const name = item.split(' | ')[0];
          const contact = contactByItem.get(item);
          const fallbackAvatar = getDefaultAvatar(name || 'Contact');
          return (
            <>
              <IonAvatar slot="start">
                <img
                  src={proxyImage(contact?.avatar ?? '') || fallbackAvatar}
                  alt={name}
                  onError={(e) => { (e.target as HTMLImageElement).src = fallbackAvatar; }}
                />
              </IonAvatar>
              <IonLabel>{name}</IonLabel>
            </>
          );
        }}
      />

      {/* Add contact — a sibling of the sheet (not nested in it). Outside the app router (the softphone) NewContact needs
          one, so it gets a throwaway MemoryRouter there, as SoftPhoneModal does. */}
      {showNewContact && (
        <IonModal isOpen={showNewContact} onDidDismiss={closeNewContact}>
          {outsideRouter ? (
            <MemoryRouter initialEntries={['/tabs/contact/new']}>
              <NewContact onClose={closeNewContact} />
            </MemoryRouter>
          ) : (
            <NewContact onClose={closeNewContact} />
          )}
        </IonModal>
      )}
    </>
  );
};

export default ContactPickerSheet;
